/* Pop Protector case picker.
   Renders on product pages tagged "pop-case" (see snippets/pop-case-picker.liquid).
   Exposes two entry points used by sections/main-product.liquid's existing
   add-to-cart handlers:
     - window.gwPopCaseSubmit(variantId, quantity, opts) — routes to the
       normal single-item flow (window.gwInterceptAddToCart, unchanged)
       when no case is selected, or to the new multi-item flow
       (window.gwInterceptAddToCartMulti, assets/gw-cart-drawer.js) when
       one is. Falls back to the plain single-item call if this file or
       the picker isn't present, so nothing on non-Funko pages changes.
     - window.gwPopCaseHasSelection() — true when a real case (not "No
       case") is currently selected, used to hide dynamic checkout buttons. */
(function () {
  // Liquid's variant.price is in the shop's smallest currency unit (e.g.
  // 499 for £4.99), same as gw-cart-drawer.js's own formatMoney — kept
  // consistent with that rather than reformatting through `money` filters.
  function formatMoney(minorUnitsStr) {
    var n = parseFloat(minorUnitsStr);
    if (isNaN(n)) return '';
    return '£' + (n / 100).toFixed(2);
  }

  function getPicker() {
    return document.querySelector('[data-pcp-picker]');
  }

  function getSectionId(picker) {
    return picker.id.replace(/^pcp-picker-/, '');
  }

  function getSelectedInput(picker) {
    return picker.querySelector('[data-pcp-input]:checked');
  }

  function getSelection() {
    var picker = getPicker();
    if (!picker) return null;
    var input = getSelectedInput(picker);
    if (!input || !input.value) return null; // "No case" has value=""
    return {
      variantId: input.value,
      price: input.dataset.pcpPrice,
      label: input.dataset.pcpLabel,
      colour: input.dataset.pcpColour || '#2ec4a9',
    };
  }

  function ensureBadge(sectionId) {
    var existing = document.getElementById('pcp-badge-' + sectionId);
    if (existing) return existing;
    // Anchored to the first/featured media item itself, not the whole
    // .product__media-wrapper — that wrapper is the full gallery column
    // (image + thumbnails + sticky behaviour), which can be taller than
    // the photo, so a badge appended straight into it was landing near
    // the top of that whole column instead of on the photo's corner.
    // The first .product__media-item is exactly the visible image's own
    // box, so anchoring there keeps the badge pinned to the photo
    // whether the gallery is sticky or the page scrolls.
    var mediaItem = document.querySelector(
      '#MainProduct-' + sectionId + ' .product__media-item.is-active'
    ) || document.querySelector('#MainProduct-' + sectionId + ' .product__media-item');
    if (!mediaItem) return null;
    mediaItem.style.position = mediaItem.style.position || 'relative';
    var badge = document.createElement('span');
    badge.id = 'pcp-badge-' + sectionId;
    badge.className = 'pcp-badge';
    // A real text node child, not an empty element — base.css's
    // div/p :empty rule doesn't apply to span anyway, but this also
    // keeps it non-empty for any future selector that does.
    badge.textContent = '';
    mediaItem.appendChild(badge);
    return badge;
  }

  function updateBadge(sectionId, selection) {
    var badge = ensureBadge(sectionId);
    if (!badge) return;
    if (selection) {
      badge.textContent = '+ ' + selection.label + ' case';
      badge.style.setProperty('--pcp-badge-colour', selection.colour);
      badge.classList.add('is-visible');
    } else {
      badge.classList.remove('is-visible');
    }
  }

  function updateSelectedText(picker, selection) {
    var el = picker.querySelector('[data-pcp-selected]');
    if (!el) return;
    if (selection) {
      el.textContent = selection.label + ' case · +' + formatMoney(selection.price);
    } else {
      el.textContent = 'No case';
    }
  }

  function updateDynamicCheckout(hasCase) {
    // Dawn's {{ form | payment_button }} renders a .shopify-payment-button
    // inside .product-form__buttons. Hiding via a class on that wrapper
    // (rather than the widget itself) keeps the widget's own script,
    // which re-renders it periodically, from silently undoing a direct
    // display:none on its own root.
    document.querySelectorAll('.product-form__buttons').forEach(function (wrap) {
      wrap.classList.toggle('pcp-hide-dynamic-checkout', hasCase);
    });
  }

  function clearError(picker) {
    var err = picker.querySelector('[data-pcp-error]');
    if (!err) return;
    err.textContent = '';
    err.classList.remove('is-visible');
  }

  function showError(picker, message) {
    var err = picker.querySelector('[data-pcp-error]');
    if (!err) return;
    err.textContent = message;
    err.classList.add('is-visible');
  }

  function onSelectionChange() {
    var picker = getPicker();
    if (!picker) return;
    var sectionId = getSectionId(picker);
    var selection = getSelection();
    updateSelectedText(picker, selection);
    updateBadge(sectionId, selection);
    updateDynamicCheckout(!!selection);
    clearError(picker);
  }

  // UK law: a paid extra must never be pre-selected. The markup already
  // only marks "No case" as checked, but browsers can restore a
  // previous selection on their own — form autofill on a plain reload,
  // or bfcache restoring live DOM state (including .checked) on
  // back/forward navigation, neither of which autocomplete="off" alone
  // reliably stops in every browser. This forces every load back to
  // "No case", including the bfcache case via the pageshow event's
  // persisted flag.
  function resetToNoCase(picker) {
    picker.querySelectorAll('[data-pcp-input]').forEach(function (input) {
      input.checked = input.value === '';
    });
  }

  function wirePicker(picker) {
    picker.addEventListener('change', function (e) {
      if (e.target && e.target.matches('[data-pcp-input]')) onSelectionChange();
    });
    resetToNoCase(picker);
    window.addEventListener('pageshow', function () {
      resetToNoCase(picker);
      onSelectionChange();
    });
    // Initial paint — "No case" is checked by default, but the badge/
    // dynamic-checkout state still needs setting on load.
    onSelectionChange();
  }

  function hasSelection() {
    return !!getSelection();
  }

  // The single entry point the product-page add-to-cart handlers call.
  // opts: { collection, sellingPlan }
  function submit(variantId, quantity, opts) {
    opts = opts || {};
    var picker = getPicker();
    var selection = picker ? getSelection() : null;

    if (!selection) {
      if (window.gwInterceptAddToCart) {
        window.gwInterceptAddToCart(variantId, quantity, opts.collection, opts.sellingPlan);
      }
      return;
    }

    if (!window.gwInterceptAddToCartMulti) {
      // Safety net: if the multi-item helper somehow isn't loaded, still
      // add the Pop rather than doing nothing, and say so.
      if (window.gwInterceptAddToCart) {
        window.gwInterceptAddToCart(variantId, quantity, opts.collection, opts.sellingPlan);
      }
      showError(picker, "Couldn't add the case — the Pop was added on its own.");
      return;
    }

    var productTitle = picker.dataset.pcpProductTitle || '';
    var items = [
      { id: parseInt(variantId, 10), quantity: quantity },
      {
        id: parseInt(selection.variantId, 10),
        quantity: quantity,
        // "For" is shown under the case line in the cart drawer;
        // "_for_variant" is the same link machine-readable for the
        // drawer's case<->Pop cascade rules (quantity capping, removal)
        // — a leading underscore keeps it hidden from Shopify's own
        // checkout line-item display, same convention as elsewhere.
        properties: { For: productTitle, _for_variant: variantId },
      },
    ];

    clearError(picker);
    window
      .gwInterceptAddToCartMulti(items)
      .catch(function (err) {
        // Only ever show err.description (a clean, Shopify- or our-own
        // friendly message) — never err.message, which could be a raw
        // JS/parse error never meant for a customer to see.
        var message = (err && err.description) || "That case just sold out — the Pop wasn't added either. Try again or pick a different case.";
        showError(picker, message);
      });
  }

  window.gwPopCaseSubmit = submit;
  window.gwPopCaseHasSelection = hasSelection;

  function init() {
    var picker = getPicker();
    if (picker) wirePicker(picker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
